import React, { useRef, useEffect, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import './TruncatedText.css'

interface TruncatedTextProps {
  text: string | null | undefined
  className?: string
  maxWidth?: string
  /** When set, the tooltip always shows on hover with this hint (appended to the full text when truncated). */
  tooltipHint?: string
}

const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  className = '',
  maxWidth,
  tooltipHint,
}) => {
  const textRef = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const [tooltipId] = useState(
    () => `truncated-${Math.random().toString(36).substring(2, 11)}`,
  )

  const displayText = text || ''

  useEffect(() => {
    const checkTruncation = (): void => {
      if (textRef.current) {
        const isOverflowing =
          textRef.current.scrollWidth > textRef.current.clientWidth
        setIsTruncated(isOverflowing)
      }
    }

    checkTruncation()
    window.addEventListener('resize', checkTruncation)
    return () => window.removeEventListener('resize', checkTruncation)
  }, [displayText])

  if (!displayText) {
    return <span className={className}>-</span>
  }

  const showTooltip = isTruncated || Boolean(tooltipHint)
  let tooltipContent: string | undefined
  if (isTruncated && tooltipHint) {
    tooltipContent = `${displayText} — ${tooltipHint}`
  } else if (isTruncated) {
    tooltipContent = displayText
  } else if (tooltipHint) {
    tooltipContent = tooltipHint
  }

  return (
    <>
      <span
        ref={textRef}
        className={`truncated-text ${className}`}
        data-tooltip-id={showTooltip ? tooltipId : undefined}
        data-tooltip-content={tooltipContent}
        style={maxWidth ? { maxWidth } : { width: '100%' }}
      >
        {displayText}
      </span>
      <Tooltip
        id={tooltipId}
        place="top"
        className="truncated-text-tooltip"
        delayShow={300}
      />
    </>
  )
}

export default TruncatedText
